import { formatName } from '@/pages/WgqAllMaps/utils';
import { formatThousandUnitDefaultTwo, keepPercentOrLine, keepTwoOrLine } from '@/utils/utils';
import {
  V2EnergyReportCostDetailPostResponse,
  V2EnergyReportTrendPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import dayjs from 'dayjs';
import { isNil } from 'lodash-es';
import { DateType } from '../../const';
import circle_spin from '../../imgs/circle_spin.png';

export const getChartOption = (data?: V2EnergyReportCostDetailPostResponse, timeResolution?: DateType) => {
  const formatStr = timeResolution === DateType.DAY ? 'MM-DD' : timeResolution === DateType.MONTH ? 'YYYY-MM' : 'YYYY';

  // 判断是否有任一费用超过1万（统一单位）
  const hasValueOverTenThousand = data?.list?.some(item => (item.value ?? 0) > 10000 || (item.planValue ?? 0) > 10000);

  const showPlan = timeResolution === DateType.MONTH || timeResolution === DateType.YEAR;
  const temperatureName = timeResolution === DateType.DAY ? '当日最高气温' : '平均气温';

  return {
    backgroundColor: 'transparent',
    grid: {
      left: 80,
      right: 40,
      top: 60,
      bottom: 10,
      containLabel: true,
    },
    legend: {
      right: 20,
      top: 0,
      bottom: 20,
      itemWidth: 16, // 宽度
      itemHeight: 10, // 高度
      data: [
        {
          name: '动能费用',
          icon: 'rect',
        },
        ...(showPlan
          ? [
              {
                name: '计划费用',
                icon: 'rect',
              },
            ]
          : []),
        {
          name: temperatureName,
          icon: 'path://M0 0 L16 0 L16 2 L0 2 Z',
        },
      ],
    },
    tooltip: {
      formatter: (items: any) => {
        let { axisValue } = items[0];
        let firstStr = dayjs(axisValue).format(formatStr);
        let otherStr = '';
        let str = '';
        items.forEach((item: any) => {
          const { data, marker, seriesName } = item;
          otherStr += `<div style="margin-top:8px;width:220px;"><div style="display:flex;justify-content:space-between;align-items:center;"><div><span>${marker}</span>${seriesName}</div><div>${
            !isNil(data?.[1]) ? keepTwoOrLine(data?.[1]) + data?.[2] : '-'
          }</div></div>`;
          str = firstStr + otherStr;
        });

        return str;
      },
    },

    color: ['#0DB6D9', showPlan ? '#FF9247' : undefined, '#FFCB47'].filter(Boolean),
    xAxis: [
      {
        type: 'time',
        minInterval:
          timeResolution === DateType.YEAR
            ? 365 * 24 * 3600 * 1000 // 按年：最小间隔1年
            : timeResolution === DateType.MONTH
            ? 30 * 24 * 3600 * 1000 // 按月：最小间隔1个月
            : 24 * 3600 * 1000, // 按日：最小间隔1天
        axisLabel: {
          formatter: function (e: number) {
            return dayjs(e).format(formatStr);
          },
          hideOverlap: true,
        },
        boundaryGap: true, // 或直接删掉
      },
    ],
    yAxis: [
      {
        type: 'value',
        alignTicks: true,
        name: '成本' + (hasValueOverTenThousand ? '（万元）' : '（元）'),
        nameTextStyle: {
          fontWeight: 500,
          fontSize: 16,
        },
      },
      {
        type: 'value',
        alignTicks: true,
        name: '温度（℃）',
      },
    ],
    series: [
      {
        type: 'bar',
        name: '计划费用',
        barMaxWidth: 20,
        data: hasValueOverTenThousand
          ? (data?.list ?? []).map(item => [item.ts, !isNil(item?.planValue) ? item.planValue / 10000 : null, '万元'])
          : (data?.list ?? []).map(item => [item.ts, item.planValue, '元']),
      },
      {
        type: 'bar',
        name: '动能费用',
        barMaxWidth: 20,
        data: hasValueOverTenThousand
          ? (data?.list ?? []).map(item => [item.ts, !isNil(item?.value) ? item.value / 10000 : null, '万元'])
          : (data?.list ?? []).map(item => [item.ts, item.value, '元']),
      },
      {
        type: 'line',
        name: temperatureName,
        data: (data?.list ?? []).map(item => [item.ts, item.temperature, '℃']),
        yAxisIndex: 1,
        // connectNulls: true,
        smooth: true,
        symbol: 'circle',
        symbolSize: 2,
        // showSymbol: false, // 只有在 tooltip hover 的时候显示symbol
        itemStyle: {
          // symbol样式
          normal: {
            borderColor: 'rgba(255, 255, 255, 0.2)', // symbol边框颜色
            borderWidth: 6, // symbol边框宽度
          },
        },
        lineStyle: {
          width: 2,
          type: 'solid', //'dotted'点型虚线 'solid'实线 'dashed'线性虚线
        },
      },
    ].filter((_, index) => !(index === 0 && !showPlan)),
  };
};
export const getCostTrendChartOption = (data?: V2EnergyReportTrendPostResponse) => {
  const formatStr = 'MM-DD';
  const valueIsOverTenThousand = data?.list
    ?.map(i => i.list?.map(c => c.value))
    .flat()
    .some(item => (item ?? 0) > 10000);
  return {
    backgroundColor: 'transparent',
    grid: {
      left: 40,
      right: 40,
      top: 30,
      bottom: 10,
      containLabel: true,
    },
    legend: {
      right: 0,
      top: 0,
      bottom: 0,
    },
    tooltip: {
      formatter: (items: any) => {
        let { axisValue } = items[0];
        let firstStr = axisValue;
        let otherStr = '';
        let str = '';
        items.forEach((item: any) => {
          const { data, marker, seriesName } = item;
          otherStr += `<div style="margin-top:8px;width:260px;"><div style="display:flex;justify-content:space-between;align-items:center;"><div style="margin-right:16px"><span>${marker}</span>${seriesName}</div><div>${
            !isNil(data?.[1]) ? keepTwoOrLine(data?.[1]) + data?.[2] : '-'
          }</div></div>`;
          str = firstStr + otherStr;
        });

        return str;
      },
    },

    color: ['#FFCB47', '#16DD8E', '#2D8DFF'],
    xAxis: [
      {
        // type: 'time',

        type: 'category',
        // data: (data?.list?.[1].list ?? []).map(a => a.ts),
        axisLabel: {
          // formatter: function (e: number) {
          //   return dayjs(e).format(formatStr);
          // },
          hideOverlap: true,
        },
        boundaryGap: true, // 或直接删掉
      },
    ],
    yAxis: [
      {
        type: 'value',
        name: valueIsOverTenThousand ? '万元' : '元',
      },
    ],
    series: (data?.list ?? []).map(item => ({
      type: 'line',
      name: `${item.year}年度动能费用累积`,
      data: valueIsOverTenThousand
        ? (item?.list ?? []).map(item => [item.ts, !isNil(item?.value) ? item.value / 10000 : null, '万元'])
        : (item?.list ?? []).map(item => [item.ts, item.value, '元']),
      // connectNulls: true,
      smooth: true,
      symbol: 'circle',
      symbolSize: 2,
      // showSymbol: false, // 只有在 tooltip hover 的时候显示symbol
      itemStyle: {
        // symbol样式
        normal: {
          borderColor: 'rgba(255, 255, 255, 0.2)', // symbol边框颜色
          borderWidth: 6, // symbol边框宽度
        },
      },
      lineStyle: {
        width: 2,
        type: 'solid', //'dotted'点型虚线 'solid'实线 'dashed'线性虚线
      },
    })),
  };
};

export const getPieChartOption = (
  list?: {
    energyMediumId?: number;
    energyMediumName?: string;
    value?: number;
    proportion?: number;
  }[]
) => {
  return {
    color: ['#2D8DFF', '#16DD8E', '#FF477B', '#FF9247', '#52E7FF', '#FFCB47', '#854ECA', '#ABD335', '#FF6D2C'],
    legend: {
      show: false,
    },
    tooltip: {
      trigger: 'item',
      formatter: (params: any) => {
        const { data } = params;
        return `
      <div>
        <div>${formatName(data?.name)}：${formatThousandUnitDefaultTwo(data?.value)}元</div>
        <div>占比：${keepPercentOrLine(data?.rate)}%</div>
      </div>
    `;
      },
    },
    graphic: {
      elements: [
        {
          type: 'image',
          style: {
            image: circle_spin,
            x: 20,
            y: 40,
            width: 80,
            height: 80,
          },
          keyframeAnimation: [
            {
              // 动画时长，单位 ms
              duration: 50000,
              // 设置旋转动画是否循环
              loop: true,
              easing: 'linear',
              // 动画的关键帧
              keyframes: [
                {
                  // percent为关键帧的位置,0为第一个,1为最后一个
                  percent: 0,
                  rotation: 0,
                  transition: ['rotation'],
                },
                {
                  percent: 1,
                  rotation: 360,
                  transition: ['rotation'],
                },
              ],
            },
          ],
          // 旋转的中心点
          origin: [60, 80],
          tooltip: {
            show: false,
          },
        },
      ],
    },
    series: [
      {
        type: 'pie',
        name: `总计`,
        percentPrecision: 5,
        minAngle: 2,
        padAngle: 1,
        radius: [40, 48],
        left: 40,
        top: 60,
        width: 40,
        height: 40,
        label: {
          show: false,
        },
        data: list?.map(item => ({
          name: item.energyMediumName,
          value: item.value,
          rate: item.proportion,
        })),
        showEmptyCircle: false,
      },
      {
        showEmptyCircle: false,
        data: list?.map(item => ({
          name: item.energyMediumName,
          value: item.value,
          rate: item.proportion,
        })),
        type: 'pie',
        percentPrecision: 5,
        padAngle: 1,
        minAngle: 2,
        name: `总计`,
        radius: [48, 60],
        left: 40,
        top: 60,
        width: 40,
        height: 40,
        emphasis: {
          disabled: true,
        },
        label: {
          show: false,
        },
        itemStyle: {
          opacity: 0.2,
        },
      },
    ],
  };
};
